/**
 * The frame: providers, the auth gate, the TOTP gate, the stream
 * lifecycle, and the router. Modules are composed here and nowhere
 * else.
 */
import { useEffect, useState } from 'react';
import { Button, ConfigProvider, Tabs, Tooltip, theme as antdTheme } from 'antd';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { connectStream, parseFilterSearch, queryClient } from '../api';
import {
  Dispatch,
  SignIn,
  TotpEnroll,
  apexUrl,
  isApexHost,
  needsTotpEnrollment,
  orgFromEmail,
  orgFromHost,
  signOutUser,
  watchUser,
  type User,
} from '../modules/auth';
import { DiffPanel } from '../modules/diff';
import { ReviewPanel } from '../modules/review';
import { RatifySectionPanel, RuleSourcesPanel } from '../modules/rules';
import { TakeoffPanel } from '../modules/takeoff';
import { BRAND, useThemeMode, type ThemeMode } from '../theme';
import { Overview } from './Overview';
import { ProjectTabs } from './ProjectTabs';
import { Workspace } from './Workspace';

const rootRoute = createRootRoute({ component: Outlet });

// Routes are the frontend's public surface; future modules contribute
// theirs and this tree composes them. The project id rides the PATH
// (APP_SPEC): every screen under a project is a link you can send.
let sessionUser: User | null = null;

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/projects' });
  },
});

// The journey's first screen (APP_SPEC box 3): sign in, see your
// projects, and every card is a link into its workspace.
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: Overview,
});

// The filter rides the search params (APP_SPEC "Filtering is
// first-class"): the RuleSelector vocabulary, typed here once;
// modules read it via useSearch without importing the shell.
const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  validateSearch: parseFilterSearch,
  component: ProjectScreen,
});

function ProjectScreen() {
  const { projectId } = projectRoute.useParams();
  return sessionUser ? <Workspace user={sessionUser} projectId={projectId} /> : null;
}

// Sibling product screens under the project param (APP_SPEC): each a
// module surface the shell frames; the filter search params carry
// across tabs unchanged.
const takeoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/takeoff',
  validateSearch: parseFilterSearch,
  component: TakeoffScreen,
});

function TakeoffScreen() {
  const { projectId } = takeoffRoute.useParams();
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/projects"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← All projects
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem' }}>
        <h1 style={{ margin: 0 }}>{projectId}</h1>
        <ProjectTabs projectId={projectId} active="takeoff" />
      </div>
      <TakeoffPanel projectId={projectId} />
    </div>
  );
}

const diffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/diff',
  validateSearch: parseFilterSearch,
  component: DiffScreen,
});

function DiffScreen() {
  const { projectId } = diffRoute.useParams();
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/projects"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← All projects
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem' }}>
        <h1 style={{ margin: 0 }}>{projectId}</h1>
        <ProjectTabs projectId={projectId} active="diff" />
      </div>
      <DiffPanel projectId={projectId} />
    </div>
  );
}

const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/review',
  validateSearch: parseFilterSearch,
  component: ReviewScreen,
});

function ReviewScreen() {
  const { projectId } = reviewRoute.useParams();
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/projects"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← All projects
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem' }}>
        <h1 style={{ margin: 0 }}>{projectId}</h1>
        <ProjectTabs projectId={projectId} active="review" />
      </div>
      <ReviewPanel
        projectId={projectId}
        signedBy={sessionUser.email ?? sessionUser.uid}
      />
    </div>
  );
}

// The front door's second step (sign in -> onboard -> upload): a
// signed-in user with no organization lands here from the projects
// screen or the upload panel's refusal message.
const onboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboard',
  component: OnboardScreen,
});

function OnboardScreen() {
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/projects"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← All projects
      </Link>
    </div>
  );
}

// Rules ingestion is org-scope, not project-scope: code books feed the
// whole ruleset, so the screen lives beside /projects, not under one.
// The tab rides the URL (shareable-view law).
const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rules',
  validateSearch: (search: Record<string, unknown>): { tab?: 'ruleset'; name?: string } => ({
    ...(search.tab === 'ruleset' ? { tab: 'ruleset' as const } : {}),
    // A just-born rule arrives highlighted (the receipt's link).
    ...(typeof search.name === 'string' && search.name !== '' ? { name: search.name } : {}),
  }),
  component: RulesScreen,
});

function RulesScreen() {
  // The ruleset tab went with its route; sections is all there is until
  // /v1/rules/ruleset comes back.
  const navigate = rulesRoute.useNavigate();
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/projects"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← All projects
      </Link>
      <h1 style={{ marginBottom: '1.2rem' }}>Rules</h1>
      <Tabs
        activeKey="sections"
        onChange={(key) =>
          void navigate({
            search: key === 'ruleset' ? { tab: 'ruleset' } : {},
            replace: true,
          })
        }
        items={[{ key: 'sections', label: 'Sections', children: <RuleSourcesPanel /> }]}
      />
    </div>
  );
}

// The ratification surface (ruled 2026-08-06): a dedicated page —
// the source text is the protagonist, so it gets the whole screen.
const ratifySectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rules/sections/$sectionId',
  component: RatifySectionScreen,
});

function RatifySectionScreen() {
  const { sectionId } = ratifySectionRoute.useParams();
  if (!sessionUser) return null;
  return (
    <div className="app-container" style={{ padding: '2rem', overflowY: 'auto' }}>
      <Link
        to="/rules"
        style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.5rem' }}
      >
        ← Rules
      </Link>
      <h1 style={{ marginBottom: '1.2rem' }}>Ratify</h1>
      <RatifySectionPanel sectionId={sectionId} />
    </div>
  );
}

const router = createRouter({
  routeTree: rootRoute.addChildren([
    indexRoute,
    projectsRoute,
    projectRoute,
    takeoffRoute,
    diffRoute,
    reviewRoute,
    rulesRoute,
    ratifySectionRoute,
    onboardRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  // undefined = auth state not yet known (avoids a sign-in flash on load)
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [enrolledTick, setEnrolledTick] = useState(0); // re-render after TOTP enrollment
  const [mode, setMode] = useThemeMode();

  useEffect(() => watchUser(setUser), []);

  // One stream per signed-in session: the api bridge invalidates
  // queries; modules subscribe for ephemeral events.
  useEffect(() => {
    if (!user) return;
    return connectStream(user);
  }, [user]);

  // EVERY SCREEN IS THEMED, which the early returns used to prevent:
  // Dispatch, SignIn and TotpEnroll all returned above the provider, so
  // the front door -- the first thing anybody sees -- rendered AntD
  // with no theme at all. The provider wraps the choice now, and the
  // choice is what varies.
  //
  // AntD is the frontend's opinion (ruling 2026-08-06): its algorithms,
  // its tokens, no theme system of our own. TWO are ours. The primary
  // colour is the MARK's, because stock blue under a teal logo reads as
  // unfinished. And the algorithm follows the machine's light/dark
  // preference unless somebody overrode it (theme/mode.ts).
  return (
    <ConfigProvider
      theme={{
        algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: { colorPrimary: BRAND.teal },
      }}
    >
      <ModeToggle mode={mode} onChange={setMode} />
      <Screen user={user} enrolledTick={enrolledTick} onEnrolled={() => setEnrolledTick(1)} />
    </ConfigProvider>
  );
}

/** Light or dark, one press. Fixed to the viewport so it reaches the
 * screens that are only a centred card -- the front door has no chrome
 * to hang it off. */
function ModeToggle({
  mode,
  onChange,
}: {
  mode: ThemeMode;
  onChange: (mode: ThemeMode | null) => void;
}) {
  const next = mode === 'dark' ? 'light' : 'dark';
  return (
    <Tooltip title={`Switch to ${next} mode`} placement="left">
      <Button
        type="text"
        aria-label={`Switch to ${next} mode`}
        onClick={() => onChange(next)}
        style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000 }}
      >
        {mode === 'dark' ? '☀' : '☾'}
      </Button>
    </Tooltip>
  );
}

/** Which screen this browser is owed, in gate order. */
function Screen({
  user,
  enrolledTick,
  onEnrolled,
}: {
  user: User | null | undefined;
  enrolledTick: number;
  onEnrolled: () => void;
}) {
  // THE FRONT DOOR, ahead of the auth gate on purpose: the apex has no
  // org, so it has no Identity Platform tenant to sign anybody in
  // against. It asks which workspace and redirects there, where the
  // subdomain selects the tenant.
  if (isApexHost(window.location.hostname)) return <Dispatch />;

  if (user === undefined) return null; // auth state still resolving
  if (user === null) return <SignIn />;

  // WRONG RECORD. Somebody with a real, valid identity for ANOTHER org
  // arrived here -- by typing the address, following a stale link, or
  // switching accounts mid-session. They go back to the front door.
  //
  // This is TIDINESS, not enforcement. core/platform/auth.py refuses
  // the token on every request, before a role or a route or a query,
  // and would keep refusing it if this block vanished. What this adds
  // is not being left staring at an app that answers 403 to everything.
  if (orgFromEmail(user.email ?? '') !== orgFromHost(window.location.hostname)) {
    void signOutUser().finally(() => window.location.assign(apexUrl(window.location)));
    return null;
  }
  // Two-factor, always: a signed-in user with no enrolled factor
  // enrolls before touching anything (emulator dev exempt).
  if (needsTotpEnrollment(user) && enrolledTick === 0) {
    return <TotpEnroll user={user} onEnrolled={onEnrolled} />;
  }

  sessionUser = user;
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
